/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// mantine
import { Box, Paper, Group, Image } from '@mantine/core';

// styles
import classes from './api-tokens.module.css';

// components
import { Pagination } from '@components/atoms/Pagination';
import { Datatable } from '@components/organisms/Datatable';
import { Button } from '@components/atoms/Button';
import { LightButton } from '@components/atoms/LightButton';
import { Text } from '@components/atoms/Text';
import { MenuFilter } from '@components/molecules/MenuFilter/MenuFilter';
import { AddFiltersButton } from '@components/atoms/AddFiltersButton';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { MultipleSelectionApiTokenModal } from '@components/molecules/MultipleSelectionApiTokenModal';
import { Tooltip } from '@components/atoms/Tooltip';
import { AddApiToken } from '@features/resources/api-tokens/components/AddApiToken';
import { AlertAccessToken } from '@features/resources/api-tokens/components/AlertAccessToken';

// i18n
import { useTranslation } from 'react-i18next';

// icons
import {
	IconCirclePlus,
	IconTrash,
} from '@tabler/icons-react';
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';

// api
import { useApiTokensApi } from '@features/resources/api-tokens/api/get-api-tokens';
import { useRemove } from '@features/resources/api-tokens/api/remove';
import { useRemoveMultiple } from '@features/resources/api-tokens/api/removeMultiple';
import { useOrganizations } from 'shared/api/get-organizations';
import { useOrganizationsByProducers } from 'shared/api/get-organizations-by-producers';

// configs
import { tableSchemaBase as columns } from '@features/resources/api-tokens/apitokens.columns';

// utils
import {
	convertArrayToLabelValue,
	getVisibleColumns,
	truncateString,
} from '@utils/functions';
import { TParsedApiToken } from '@features/resources/api-tokens/types';
import { filterEntitiesByIDs } from '@features/resources/api-tokens/utils/functions';
import { DrawerApiToken } from '@features/resources/api-tokens/components/DrawerApiToken';
import { useApiTokens } from '@features/resources/api-tokens/hooks/useApiTokens';
import { ResetButton } from '@components/atoms/ResetButton';
import { TTargetColumn } from '@global-types/index';
import {
	Authorization,
	useAuthorization,
} from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const ApiTokens = () => {
	const { t } = useTranslation();

	const {
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		openDrawerAddApiToken,
		setOpenDrawerAddApiToken,
		handleAddItem,
		handleDeleteMultipleItems,
		ref,
		apiTokensDatatableOptions,
		setApiTokensDatatableOptions,
		containerRef,
		handleScroll,
		handleOrganizationFilter,
		openDrawer,
		setOpenDrawer,
		handleCloseConfirmationDeleteItem,
		handleConfirmDeleteItem,
		handleOpenConfirmationDeleteItem,
		handleConfirmDeleteMultipleItem,
		handleViewMore,
		handleRowClicked,
		handleSelectedRecordsChange,
		handleSortStatusChange,
		isOverflowFilters,
		handlepaginationchangesize,
		handlechangepagination,
		apiTokensPage,
		apiTokensPageSize,
		apiTokensSelectedRecords,
		setApiTokensSelectedRecords,
		apiTokensOpenConfirmationModalDelete,
		setApiTokensOpenConfirmationModalDelete,
		apiTokensQueryParams,
		apiTokensSortStatus,
		apiTokensSortParams,
		apiTokensSelectedRow,
		apiTokensLastAccessTokenCreated,
		setApiTokensLastAccessTokenCreated,
		handleResetSelectedItems,
	} = useApiTokens();

	// APIs
	const { data: organizations } = useOrganizations();
	const { data: organizationsByProducersIDs } =
		useOrganizationsByProducers();

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useApiTokensApi(
		apiTokensPage,
		apiTokensPageSize,
		apiTokensQueryParams,
		apiTokensSortParams
	);

	const remove = useRemove(
		apiTokensPage,
		apiTokensPageSize
	);

	const removeMultiple = useRemoveMultiple(
		apiTokensPage,
		apiTokensPageSize
	);

	const IS_EMPTY = apiTokensSelectedRecords
		? apiTokensSelectedRecords.length === 0
		: true;

	const NBR_SELECTED_ROWS =
		apiTokensSelectedRecords.length;

	const { checkAcl, checkAccess } = useAuthorization();

	return (
		<Box className={classes.container} ref={ref}>
			{/*Drawer apiToken */}
			<AddApiToken
				opened={openDrawerAddApiToken}
				onClose={() => {
					setOpenDrawerAddApiToken(false);
				}}
			/>
			{/*Drawer apiToken */}
			<DrawerApiToken
				opened={openDrawer}
				handledelete={handleOpenConfirmationDeleteItem}
				onClose={() => {
					setOpenDrawer(false);
				}}
				position={'right'}
				data={apiTokensSelectedRow}
			/>
			{/* Delete Multiple selection modal */}
			<MultipleSelectionApiTokenModal
				type="delete"
				data={apiTokensSelectedRecords}
				onConfirm={() =>
					handleConfirmDeleteMultipleItem(removeMultiple)
				}
				onClose={() => {
					setOpenSelectedItemsToDeleteModal(false);
				}}
				onRemove={(item) => {
					const newList = [
						...apiTokensSelectedRecords,
					].filter((apiToken) => apiToken.id !== item.id);

					setApiTokensSelectedRecords(newList);
				}}
				opened={openSelectedItemsToDeleteModal}
			/>

			{/*Delete confirmation modal */}
			<ConfirmationModal
				title={t(
					'apiTokens.alert.confirmationDelete.title'
				)}
				description={t(
					'apiTokens.alert.confirmationDelete.description'
				)}
				onConfirm={() => handleConfirmDeleteItem(remove)}
				onClose={handleCloseConfirmationDeleteItem}
				opened={
					apiTokensOpenConfirmationModalDelete &&
					apiTokensOpenConfirmationModalDelete.open
				}
			/>

			<Paper className={classes.paper}>
				{/* Row filters */}
				<Box className={classes.containerFilters}>
					<Box className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<Box
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Authorization
										allowedRoles={[
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										]}
									>
										<Box
											className={
												classes.containerFilterSelect
											}
										>
											<MenuFilter
												onApply={handleOrganizationFilter}
												label="organizations"
												size="lg"
												options={convertArrayToLabelValue(
													filterEntitiesByIDs(
														organizations,
														organizationsByProducersIDs
													)
												)}
												placeholderSearch={t(
													'filters.placeholderSearch.searchOrganization'
												)}
												placeholder={t(
													'filters.placeholders.selectOrganization'
												)}
											/>
										</Box>
										<Box
											className={classes.spacerFilter}
										/>
										<Box
											className={
												classes.containerFilterNumeric
											}
										>
											<AddFiltersButton>
												{t('global.addFilters')}
											</AddFiltersButton>
										</Box>
									</Authorization>
								</Box>
							</Group>
						</Box>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</Box>
					{checkAcl(ACLS.API_TOKENS.CREATE) &&
						checkAccess({
							allowedRoles: [
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							],
						}) && (
							<div className={classes.wrapperRowRight}>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									hiddenFrom="xxl"
									size="md"
									onClick={handleAddItem}
								>
									{t('apiTokens.addApiTokens')}
								</Button>
								<Button
									leftSection={
										<IconCirclePlus size={23} />
									}
									visibleFrom="xxl"
									size="lg"
									onClick={handleAddItem}
								>
									{t('apiTokens.addApiTokens')}
								</Button>
							</div>
						)}
				</Box>
				<Box className={classes.spacerRow} />
				{/* Row actions */}
				{checkAcl(ACLS.API_TOKENS.DELETE) && (
					<Box className={classes.containerRow}>
						<Group
							gap={'10px'}
							className={classes.containerRowLeft}
						>
							<Tooltip
								label={NBR_SELECTED_ROWS.toString()}
								isActive={
									NBR_SELECTED_ROWS.toString().length > 4
								}
								position={'left'}
							>
								<Box
									className={
										classes.containerNbrSelection
									}
								>
									<Text
										type="body2"
										color="inputs-texts-icons"
										size="xs"
									>
										{'('}
										{apiTokensSelectedRecords
											? truncateString(
													NBR_SELECTED_ROWS.toString(),
													4
												)
											: 0}
										{') '}
										{t('global.selected')}
									</Text>
									{/* Reset selection action */}
									<ResetButton
										disabled={
											apiTokensSelectedRecords &&
											apiTokensSelectedRecords.length == 0
										}
										variant="transparent"
										size="xs"
										onClick={handleResetSelectedItems}
									/>
								</Box>
							</Tooltip>
							<LightButton
								disabled={IS_EMPTY}
								leftSection={<IconTrash size={18} />}
								size="lg"
								visibleFrom="xxl"
								onClick={handleDeleteMultipleItems}
							>
								{t('global.delete')}
							</LightButton>
							<LightButton
								disabled={IS_EMPTY}
								leftSection={<IconTrash size={18} />}
								size="md"
								hiddenFrom="xxl"
								onClick={handleDeleteMultipleItems}
							>
								{t('global.delete')}
							</LightButton>
						</Group>
					</Box>
				)}
				<Box className={classes.spacerRow} />
				{apiTokensLastAccessTokenCreated ? (
					<AlertAccessToken
						accessToken={apiTokensLastAccessTokenCreated}
						handleclose={() =>
							setApiTokensLastAccessTokenCreated(null)
						}
					/>
				) : null}
				<Box className={classes.spacerRow} />
				{/* Datatable */}
				<Box className={classes.containerDatatable}>
					<Datatable
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							setApiTokensDatatableOptions(
								getVisibleColumns(columns, cols)
							);
						}}
						loading={
							isLoading ? isLoading.toString() : undefined
						}
						allColumns={columns}
						columns={apiTokensDatatableOptions}
						data={data}
						onRowClick={handleRowClicked}
						onViewMore={handleViewMore}
						onRemoveItem={(row: TParsedApiToken) => {
							setApiTokensOpenConfirmationModalDelete({
								open: true,
								params: row,
							});
						}}
						style={{
							minHeight: '100%',
						}}
						height={250}
						selectedRecords={apiTokensSelectedRecords}
						onSelectedRecordsChange={
							handleSelectedRecordsChange
						}
						onSortStatusChange={handleSortStatusChange}
						sortstatus={apiTokensSortStatus}
					/>
				</Box>
			</Paper>
			{/* Pagination */}
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={apiTokensPageSize}
					totalitems={totalItems}
					activepage={apiTokensPage}
					handlechangesize={handlepaginationchangesize}
					onChange={handlechangepagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default ApiTokens;
